"use client";

import type { PricingMapEntries } from "@frontend/lib/stores/slices/pricingMapSlice";
import { cn } from "@frontend/shared/lib/utils";
import type { HrtOption } from "./ProductContext";

interface InitialFollowUpSelectorProps {
  initialOptions: HrtOption[];
  selectedHrtId: number | undefined;
  onHrtSelect: (hrtId: number | undefined) => void;
  pricingMap: PricingMapEntries;
  calculatePrice: (option: HrtOption, pricingMap: PricingMapEntries) => number;
}

export function InitialFollowUpSelector({
  initialOptions,
  selectedHrtId,
  onHrtSelect,
  pricingMap,
  calculatePrice,
}: InitialFollowUpSelectorProps) {
  const options = [
    ...initialOptions,
    {
      hrt: {
        hrtId: -1,
        name: "Follow-up Visit",
        isConsumerFacing: true,
        hrtPaymentType: [],
        displayMetadata: {},
        popularity: 0,
      },
      product_component_type: "INITIAL_REQUIRED",
    } as HrtOption,
  ];

  return (
    <div className="grid w-full grid-cols-5 gap-4">
      {options.map((option) => {
        // Follow-up option
        if (option.hrt.hrtId === -1) {
          return (
            <button
              key="follow-up"
              type="button"
              onClick={() => onHrtSelect(undefined)}
              className={cn(
                "flex flex-col gap-1 border rounded-[20px] px-5 py-4 text-left transition-colors",
                selectedHrtId === undefined
                  ? "border-primary bg-primary/5 col-span-3"
                  : "hover:border-primary/50 col-span-2",
              )}
            >
              <p className="font-bold text-sm">Follow-up</p>
            </button>
          );
        }

        // Initial visit options
        const price = calculatePrice(option, pricingMap);
        return (
          <button
            key={option.hrt.hrtId}
            type="button"
            onClick={() => onHrtSelect(option.hrt.hrtId)}
            className={cn(
              "flex gap-1 border rounded-[20px] px-5 py-4 text-left transition-colors",
              selectedHrtId === option.hrt.hrtId
                ? "border-primary bg-primary/5 col-span-3"
                : "hover:border-primary/50 col-span-2",
            )}
          >
            <div className="flex gap-2 justify-between w-full">
              <p className="text-sm font-bold">New Patient</p>
              {selectedHrtId === option.hrt.hrtId && (
                <p className="text-sm font-mono">
                  {Number.isNaN(price) ? "Loading..." : `+ $${price}`}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
